import React, { useCallback, useEffect, useState } from "react";
import { getCiCompetitors } from "../../api/competitiveIntelligence";
import CompetitorDetailDrawer from "./CompetitorDetailDrawer";
import CompetitorPicker from "./CompetitorPicker";
import { InferredOccupancy, titleCaseName } from "../../utils/ciFormat";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import { Skeleton } from "../shared/Skeleton";
import { MONTHS, fmtRate } from "./shared";

// Plain-string parse to avoid the UTC timezone shift `new Date(iso).toLocaleDateString()` causes.
const fmtAddedDate = (iso) => {
  if (!iso) return null;
  const ymd = iso.slice(0, 10);
  const [year, month, day] = ymd.split("-");
  if (!year || !month || !day) return null;
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

const fmtMiles = (value) => (value == null ? "—" : `${Number(value).toFixed(1)} mi`);
const fmtRating = (value) => (value == null ? "—" : Number(value).toFixed(1));
const fmtReviewCount = (value) => {
  if (value == null) return "";
  const count = Number(value);
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(count);
};

const SOURCE_LABELS = {
  campspot: "Campspot",
  airbnb: "Airbnb",
  hotel: "Hotel",
  hipcamp: "Hipcamp",
  glampinghub: "GlampingHub",
};
const sourceLabel = (source) => SOURCE_LABELS[source] ?? (source ? source.charAt(0).toUpperCase() + source.slice(1) : "—");

// Per-platform pill colors. Keep these tinted/subtle so they read as identity, not status.
// Hotel and Airbnb follow the spec tier palette (blue #3B82F6, pink #EC4899).
// glampinghub's violet-500 matches spec purple #8B5CF6. campspot/hipcamp have
// no tier mapping in the spec — kept as-is for platform identity.
const SOURCE_PILL = {
  campspot:    "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20",
  airbnb:      "bg-pink-50 text-pink-700 ring-pink-200 dark:bg-pink-500/10 dark:text-pink-300 dark:ring-pink-500/20",
  hipcamp:     "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
  glampinghub: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
  hotel:       "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20",
};
const sourcePillClass = (source) =>
  SOURCE_PILL[source] ?? "bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700";

const HeaderRow = ({ count, onAddCompetitors }) => {
  const isOne = count === 1;
  return (
    <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
          {isOne ? "Competitor Profile" : "Competitor Profiles"}
          <InfoTip text="Direct competitors your team mapped, each with avg rate, avg occupancy, distance, rating, and PMS source. Click a card for a per-category breakdown and rate history." />
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {isOne
            ? "Direct competitor tracked against your competitive set, with per-category rate summary."
            : "Direct competitors tracked against your competitive set, with per-category rate summaries."}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {count > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-semibold text-gray-700 dark:text-gray-200">{count}</span> {isOne ? "competitor" : "competitors"}
          </div>
        )}
        <button
          type="button"
          onClick={onAddCompetitors}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add competitors
        </button>
      </div>
    </div>
  );
};

const StatusBadge = ({ status, firstDataAt }) => {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Active
      </span>
    );
  }
  if (status === "pending") {
    const added = fmtAddedDate(firstDataAt);
    return (
      <div className="flex flex-col items-end gap-0.5">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Awaiting Data
        </span>
        {added && <span className="text-[10px] text-gray-500 dark:text-gray-400">Added {added}</span>}
      </div>
    );
  }
  if (status === "no_source") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
        Not on supported platform
      </span>
    );
  }
  return null;
};

const StarIcon = ({ className = "w-3.5 h-3.5 text-amber-500" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l2.9 6.93 7.1.62-5.4 4.74L18.18 22 12 18.27 5.82 22l1.58-7.71L2 9.55l7.1-.62L12 2z" />
  </svg>
);

// Pulls a single "headline" rate-summary row out of the category breakdown so the card has one
// number to show. Prefers the first entry but caller can pass a category preference if needed.
const pickHeadlineSummary = (rateSummary) => {
  if (!Array.isArray(rateSummary) || rateSummary.length === 0) return null;
  return rateSummary[0];
};

const CompetitorCard = ({ competitor, onSelect }) => {
  const summary = pickHeadlineSummary(competitor.rate_summary);
  const status = competitor.data_status;
  const isActive = status === "active";
  const location = [competitor.competitor_city, competitor.competitor_state].filter(Boolean).join(", ");
  const addedDate = fmtAddedDate(competitor.first_data_at);

  // Title-case ALL CAPS competitor names from Atlas and expose the full
  // display name as a hover tooltip for truncated long names (to-do B6).
  const rawName = competitor.competitor_name || "";
  const displayName = rawName ? titleCaseName(rawName) : "—";

  // Active cards open the detail drawer; pending/no_source have nothing more to show.
  const clickable = isActive;
  const handleClick = clickable ? () => onSelect(competitor) : undefined;
  const handleKeyDown = clickable
    ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onSelect(competitor); } }
    : undefined;

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`group bg-gray-50 dark:bg-gray-800/40 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 flex flex-col transition ${
        clickable
          ? "cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/40"
          : ""
      }`}
    >
      <div className="p-4 sm:p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate min-w-0" title={rawName ? displayName : undefined}>
            {displayName}
          </h3>
          <StatusBadge status={status} firstDataAt={competitor.first_data_at} />
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-x-2 gap-y-1 flex-wrap">
          {competitor.competitor_google_rating != null && (
            <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200">
              <StarIcon className="w-3 h-3 text-amber-500" />
              <span className="font-medium tabular-nums">{fmtRating(competitor.competitor_google_rating)}</span>
              {competitor.competitor_google_review_count != null && (
                <span className="text-gray-500 dark:text-gray-400">({fmtReviewCount(competitor.competitor_google_review_count)})</span>
              )}
            </span>
          )}
          {competitor.distance_miles != null && (
            <>
              <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
              <span>{fmtMiles(competitor.distance_miles)}</span>
            </>
          )}
          {location && (
            <>
              <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
              <span>{location}</span>
            </>
          )}
          <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
          <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${sourcePillClass(competitor.competitor_source || competitor.atlas_source_name)}`}>
            {sourceLabel(competitor.competitor_source || competitor.atlas_source_name)}
          </span>
        </div>

        {isActive && summary && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div>
              <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Avg Rate</div>
              <div className="mt-0.5 text-base font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                {fmtRate(summary.avg_rate)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Avg Occupancy</div>
              <div className="mt-0.5 text-base font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                <InferredOccupancy value={summary.avg_occupancy} />
              </div>
            </div>
          </div>
        )}

        {/* Fill the empty space on non-active cards so they don't look broken */}
        {status === "pending" && (
          <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-start gap-2">
              <svg viewBox="0 0 20 20" className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.39.22.53l3 3a.75.75 0 101.06-1.06L10.75 9.69V5z" clipRule="evenodd" />
              </svg>
              <div>
                Rate data appears after this competitor's first sync.
                {addedDate && <div className="mt-0.5 text-gray-400 dark:text-gray-500">Tracking since {addedDate}</div>}
              </div>
            </div>
          </div>
        )}

        {status === "no_source" && (
          <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-start gap-2">
              <svg viewBox="0 0 20 20" className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>Not listed on Campspot, Airbnb, Hipcamp, or supported platforms — rates can't be tracked automatically.</span>
            </div>
          </div>
        )}
      </div>

      {clickable && (
        <div className="px-4 sm:px-5 py-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-1 text-xs text-gray-500 dark:text-gray-400 group-hover:text-[#32B1FF] transition-colors">
          <span>View details</span>
          <svg viewBox="0 0 20 20" className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M7 5l6 5-6 5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </div>
  );
};

const SkeletonCard = () => (
  <div className="bg-gray-50 dark:bg-gray-800/40 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-5">
    <Skeleton className="h-3 w-20" />
    <Skeleton className="h-4 w-2/3 mt-2" />
    <Skeleton className="h-3 w-1/2 mt-2" />
    <Skeleton className="h-12 mt-4" />
  </div>
);

const CompetitorProfilesPanel = ({ clientId }) => {
  const [state, setState] = useState({ loading: false, error: "", data: null });
  const [selected, setSelected] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadCompetitors = useCallback(() => {
    if (!clientId) return;
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    getCiCompetitors(clientId)
      .then((resp) => {
        if (!cancelled) {
          const real = Array.isArray(resp?.data) ? resp.data : [];
          setState({ loading: false, error: "", data: real });
        }
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err?.message || "Failed to load competitors", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    loadCompetitors();
  }, [loadCompetitors]);

  // display_order ascending (1 is closest/most relevant); falls back to distance for ties.
  const competitors = [...(state.data || [])]
    .sort((competitorA, competitorB) => {
      const orderA = competitorA.display_order ?? 999;
      const orderB = competitorB.display_order ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      const distanceA = competitorA.distance_miles ?? Number.POSITIVE_INFINITY;
      const distanceB = competitorB.distance_miles ?? Number.POSITIVE_INFINITY;
      return distanceA - distanceB;
    });

  if (state.loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
        <HeaderRow count={0} onAddCompetitors={() => setPickerOpen(true)} />
        <div className="p-4 sm:p-5 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
        <HeaderRow count={0} onAddCompetitors={() => setPickerOpen(true)} />
        <ErrorState variant="inset" padding="px-5 py-6" message={state.error} />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
      <HeaderRow count={competitors.length} onAddCompetitors={() => setPickerOpen(true)} />
      <div className="p-4 sm:p-5">
        {competitors.length === 0 ? (
          <div className="py-4 space-y-3">
            <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
              <div className="font-medium text-gray-700 dark:text-gray-200">No direct competitors configured</div>
              <div className="text-xs">
                Add the parks you compete with so rate, occupancy, and demand panels have data to compare against.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add competitors
            </button>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
            {competitors.map((competitor) => (
              <CompetitorCard key={competitor.id} competitor={competitor} onSelect={setSelected} />
            ))}
          </div>
        )}
      </div>

      <CompetitorDetailDrawer
        open={!!selected}
        competitor={selected}
        clientId={clientId}
        onClose={() => setSelected(null)}
      />

      <CompetitorPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        clientId={clientId}
        onRosterChanged={loadCompetitors}
      />
    </div>
  );
};

export default CompetitorProfilesPanel;
